import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MASTER_LOGIN_ALIAS, MASTER_LOGIN_EMAIL } from '../utils/masterAccess';
import { AuthShell } from '../components/AuthShell';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const { signIn, resetPassword } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const es = language === 'es';

  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // 'login' = the form; 'forgot' = ask for the email to send the reset link to;
  // 'sent' = the reset mail is on its way.
  const [mode, setMode] = useState('login');

  const redirectTo = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    const isMasterAlias = trimmedEmail.toLowerCase() === MASTER_LOGIN_ALIAS;

    if (!isMasterAlias && !EMAIL_RE.test(trimmedEmail)) {
      setError(
        es
          ? 'Ingresá tu email (o "master" si tenés acceso Master)'
          : 'Enter your email (or "master" if you have Master access)'
      );
      return;
    }

    setLoading(true);
    try {
      await signIn(isMasterAlias ? MASTER_LOGIN_EMAIL : trimmedEmail, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(
        /email not confirmed/i.test(err?.message || '')
          ? (es
            ? 'Todavía no confirmaste tu email. Revisá tu casilla (y el correo no deseado) y tocá el enlace.'
            : 'You have not confirmed your email yet. Check your inbox (and spam) and tap the link.')
          : (es ? 'Email o contraseña incorrectos' : 'Incorrect email or password')
      );
    }
    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedEmail = email.trim();
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError(es ? 'Ingresá el email de tu cuenta' : 'Enter the email of your account');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(trimmedEmail);
      setMode('sent');
    } catch (err) {
      setError(
        /rate limit|too many|security purposes/i.test(err?.message || '')
          ? (es ? 'Ya pediste un enlace hace un momento. Esperá un minuto y probá de nuevo.' : 'You asked for a link a moment ago. Wait a minute and try again.')
          : (es ? 'No se pudo enviar el email. Revisá tu conexión y probá de nuevo.' : 'The email could not be sent. Check your connection and try again.')
      );
    }
    setLoading(false);
  };

  const backToLogin = () => { setMode('login'); setError(''); };

  if (mode === 'sent') {
    return (
      <AuthShell title={es ? '¡Revisá tu email!' : 'Check your email!'}>
        <p className="fp-msg fp-msg--ok">
          {es
            ? `Si hay una cuenta con ${email.trim()}, te enviamos un enlace para elegir una contraseña nueva.`
            : `If there is an account for ${email.trim()}, we sent you a link to choose a new password.`}
        </p>
        <p style={styles.help}>
          {es
            ? 'Abrí el enlace y elegí tu contraseña nueva. Si no lo ves, mirá en correo no deseado.'
            : 'Open the link and choose your new password. If you do not see it, check your spam folder.'}
        </p>
        <button type="button" className="fp-btn fp-btn--lg fp-btn--block" onClick={backToLogin}>
          {es ? 'Volver a Iniciar Sesión' : 'Back to Log In'}
        </button>
      </AuthShell>
    );
  }

  if (mode === 'forgot') {
    return (
      <AuthShell
        title={es ? 'Recuperar contraseña' : 'Reset password'}
        subtitle={es
          ? 'Escribí el email de tu cuenta y te enviamos un enlace para elegir una contraseña nueva.'
          : 'Type the email of your account and we will send you a link to choose a new password.'}
        onSubmit={handleForgotSubmit}
      >
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="fp-input"
          required
          autoFocus
          data-testid="forgot-email-input"
        />

        {error && <div className="fp-msg fp-msg--error">{error}</div>}

        <button type="submit" className="fp-btn fp-btn--gold fp-btn--lg fp-btn--block" disabled={loading} data-testid="forgot-submit-btn">
          {loading ? '...' : es ? 'Enviar enlace' : 'Send link'}
        </button>

        <button type="button" className="fp-link" style={styles.center} onClick={backToLogin}>
          {es ? '← Volver' : '← Back'}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={es ? 'Iniciar Sesión' : 'Log In'} onSubmit={handleSubmit}>
      <input
        type="text"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        className="fp-input"
        required
        autoFocus
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={es ? 'Contraseña' : 'Password'}
        className="fp-input"
        required
      />

      {error && <div className="fp-msg fp-msg--error">{error}</div>}

      <button type="submit" className="fp-btn fp-btn--lg fp-btn--block" disabled={loading}>
        {loading ? '...' : es ? 'Entrar' : 'Log In'}
      </button>

      <button
        type="button"
        className="fp-link"
        style={styles.center}
        onClick={() => { setMode('forgot'); setError(''); }}
        data-testid="forgot-password-btn"
      >
        {es ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
      </button>

      <p style={styles.switchText}>
        {es ? '¿No tenés cuenta?' : "Don't have an account?"}{' '}
        <Link to="/signup" style={styles.switchLink}>
          {es ? 'Creá una' : 'Sign up'}
        </Link>
      </p>
    </AuthShell>
  );
}

const styles = {
  center: {
    alignSelf: 'center',
  },
  help: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.45,
    color: '#eadcff',
    textAlign: 'center',
  },
  switchText: {
    fontSize: '13.5px',
    color: '#eadcff',
    textAlign: 'center',
    margin: 0,
  },
  switchLink: {
    color: '#ffd400',
    fontWeight: 900,
    textDecoration: 'none',
  },
};
